// @ts-nocheck
import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Users,
  Clock,
  Award,
  BarChart3,
  PieChart,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { analyticsApi, examsApi, attemptsApi } from '@/api/endpoints';
import { StatCard, CircularProgress } from '@azyntor-labs/nexus-ui';
import type { ExamAttempt } from '@/types';

export const ExamAnalyticsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Time series date range state
  const [timeSeriesRange, setTimeSeriesRange] = useState({ days: 30 });
  const startDate = new Date(Date.now() - timeSeriesRange.days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const endDate = new Date().toISOString().split('T')[0];

  // Fetch exam details
  const { data: examResponse, isLoading: isLoadingExam } = useQuery({
    queryKey: ['exam', id],
    queryFn: () => examsApi.get(id!),
    enabled: !!id,
  });

  // Fetch exam analytics
  const { data: analyticsResponse, isLoading: isLoadingAnalytics } = useQuery({
    queryKey: ['exam-analytics', id],
    queryFn: () => analyticsApi.getExamAnalytics(id!),
    enabled: !!id,
  });

  // Fetch exam time series data
  const { data: timeSeriesResponse } = useQuery({
    queryKey: ['exam-time-series', id, startDate, endDate],
    queryFn: () => analyticsApi.getExamTimeSeries(id!, { start_date: startDate, end_date: endDate }),
    enabled: !!id,
  });

  // Fetch violations by exam
  const { data: violationsResponse } = useQuery({
    queryKey: ['exam-violations', id],
    queryFn: () => analyticsApi.getViolationsByExam(id!),
    enabled: !!id,
  });

  // Fetch actual attempt data for this exam (teacher/admin endpoint)
  const { data: attemptsResponse } = useQuery({
    queryKey: ['exam-analytics-attempts', id],
    queryFn: () => attemptsApi.listAllByExam(id!, { limit: 100, offset: 0 }),
    enabled: !!id,
  });

  const timeSeries = timeSeriesResponse?.data?.data || [];
  const examViolations = violationsResponse?.data?.data;

  const exam = examResponse?.data.data;
  const analytics = analyticsResponse?.data.data;

  // Extract and sort attempts by percentage descending (highest score first)
  const sortedAttempts = useMemo(() => {
    const raw: ExamAttempt[] = attemptsResponse?.data?.data?.data || [];
    // Only show completed/graded/submitted attempts with scores
    const completed = raw.filter(
      (a) => a.status === 'submitted' || a.status === 'graded' || a.status === 'terminated'
    );
    return [...completed].sort((a, b) => (b.percentage ?? 0) - (a.percentage ?? 0));
  }, [attemptsResponse]);

  if (isLoadingExam || isLoadingAnalytics) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (!exam || !analytics) {
    return (
      <div className="max-w-2xl mx-auto mt-8">
        <div className="glass-card border-red-500/30 p-6">
          <h3 className="text-lg font-semibold text-red-400">Exam Not Found</h3>
          <p className="text-red-400/70 mt-1">The exam analytics you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  // Calculate score ranges for distribution
  const scoreRanges = [
    { range: '0-20%', count: 0, color: 'bg-red-500' },
    { range: '21-40%', count: 0, color: 'bg-orange-500' },
    { range: '41-60%', count: 0, color: 'bg-yellow-500' },
    { range: '61-80%', count: 0, color: 'bg-blue-500' },
    { range: '81-100%', count: 0, color: 'bg-green-500' },
  ];

  // Populate score distribution from analytics API or compute from real attempts
  if (analytics.score_distribution && analytics.score_distribution.length > 0) {
    analytics.score_distribution.forEach((item: { range: string; count: number }) => {
      const range = scoreRanges.find((r) => r.range === item.range);
      if (range) range.count = item.count;
    });
  } else if (sortedAttempts.length > 0) {
    // Compute from real attempt data as fallback
    for (const attempt of sortedAttempts) {
      const pct = attempt.percentage ?? 0;
      if (pct < 20) scoreRanges[0].count++;
      else if (pct < 40) scoreRanges[1].count++;
      else if (pct < 60) scoreRanges[2].count++;
      else if (pct < 80) scoreRanges[3].count++;
      else scoreRanges[4].count++;
    }
  }

  const maxCount = Math.max(...scoreRanges.map((r) => r.count), 1);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/analytics')}
          className="flex items-center space-x-2 text-white/50 hover:text-white mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Analytics</span>
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">{exam.title}</h1>
            <p className="text-white/50">
              Detailed performance analytics and insights
            </p>
          </div>

          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              exam.status === 'completed'
                ? 'bg-white/10 text-white/70'
                : exam.status === 'active'
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'bg-blue-500/20 text-blue-400'
            }`}
          >
            {exam.status.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Attempts"
          value={analytics.total_attempts}
          icon={<Users />}
          subtitle={`${analytics.completed_attempts} completed`}
          color="blue"
        />

        <StatCard
          title="Average Score"
          value={`${analytics.average_score?.toFixed(1)}%`}
          icon={<Award />}
          trend={analytics.average_score >= 70 ? "up" : "down"}
          trendValue={`Range: ${analytics.lowest_score} - ${analytics.highest_score}`}
          color="green"
          chart={<CircularProgress value={analytics.average_score || 0} size={48} />}
        />

        <StatCard
          title="Pass Rate"
          value={`${analytics.pass_rate?.toFixed(0)}%`}
          icon={<CheckCircle />}
          subtitle={`${exam.passing_marks ?? exam.passing_score ?? 0}% passing score`}
          color="purple"
          chart={<CircularProgress value={analytics.pass_rate || 0} size={48} />}
        />

        <StatCard
          title="Avg Time (min)"
          value={analytics.average_time_minutes?.toFixed(0)}
          icon={<Clock />}
          subtitle={`${exam.duration ?? exam.duration_minutes ?? 0} min allowed`}
          color="orange"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        {/* Score Distribution */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white">Score Distribution</h2>
            <BarChart3 className="w-5 h-5 text-white/40" />
          </div>

          <div className="space-y-4">
            {scoreRanges.map((range) => (
              <div key={range.range}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-white/70">{range.range}</span>
                  <span className="text-sm text-white/50">{range.count} students</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-3">
                  <div
                    className={`${range.color} h-3 rounded-full transition-all duration-300`}
                    style={{ width: `${(range.count / maxCount) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Difficulty Distribution */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white">Question Difficulty</h2>
            <PieChart className="w-5 h-5 text-white/40" />
          </div>

          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full" />
                  <span className="text-sm font-medium text-white/70">Easy</span>
                </span>
                <span className="text-sm text-white/50">
                  {analytics.difficulty_distribution?.easy || 0} questions
                </span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-3">
                <div
                  className="bg-emerald-500 h-3 rounded-full"
                  style={{
                    width: `${
                      ((analytics.difficulty_distribution?.easy || 0) /
                        ((analytics.difficulty_distribution?.easy || 0) +
                          (analytics.difficulty_distribution?.medium || 0) +
                          (analytics.difficulty_distribution?.hard || 0))) *
                      100
                    }%`,
                  }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-amber-500 rounded-full" />
                  <span className="text-sm font-medium text-white/70">Medium</span>
                </span>
                <span className="text-sm text-white/50">
                  {analytics.difficulty_distribution?.medium || 0} questions
                </span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-3">
                <div
                  className="bg-amber-500 h-3 rounded-full"
                  style={{
                    width: `${
                      ((analytics.difficulty_distribution?.medium || 0) /
                        ((analytics.difficulty_distribution?.easy || 0) +
                          (analytics.difficulty_distribution?.medium || 0) +
                          (analytics.difficulty_distribution?.hard || 0))) *
                      100
                    }%`,
                  }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full" />
                  <span className="text-sm font-medium text-white/70">Hard</span>
                </span>
                <span className="text-sm text-white/50">
                  {analytics.difficulty_distribution?.hard || 0} questions
                </span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-3">
                <div
                  className="bg-red-500 h-3 rounded-full"
                  style={{
                    width: `${
                      ((analytics.difficulty_distribution?.hard || 0) /
                        ((analytics.difficulty_distribution?.easy || 0) +
                          (analytics.difficulty_distribution?.medium || 0) +
                          (analytics.difficulty_distribution?.hard || 0))) *
                      100
                    }%`,
                  }}
                />
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-white/[0.06]">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-emerald-400">
                  {analytics.difficulty_distribution?.easy || 0}
                </div>
                <div className="text-xs text-white/50">Easy</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-amber-400">
                  {analytics.difficulty_distribution?.medium || 0}
                </div>
                <div className="text-xs text-white/50">Medium</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-400">
                  {analytics.difficulty_distribution?.hard || 0}
                </div>
                <div className="text-xs text-white/50">Hard</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Table */}
      <div className="glass-card p-6 mb-8">
        <h2 className="text-lg font-semibold text-white mb-6">
          Student Performance
          {sortedAttempts.length > 0 && (
            <span className="text-sm font-normal text-white/50 ml-2">
              ({sortedAttempts.length} attempt{sortedAttempts.length !== 1 ? 's' : ''})
            </span>
          )}
        </h2>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Rank</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">
                  Student
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Score</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">
                  Percentage
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Time</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.06]">
              {sortedAttempts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-white/40 text-sm">
                    No completed attempts yet
                  </td>
                </tr>
              ) : (
                sortedAttempts.slice(0, 50).map((attempt, idx) => {
                  const totalMarks = exam.total_marks ?? exam.total_points ?? 0;
                  const pct = attempt.percentage ?? 0;
                  const rawScore = attempt.score ?? 0;
                  const passed = attempt.result === 'pass';

                  // Calculate time taken from start_time to end_time or submit_time
                  let timeTakenMin: number | null = null;
                  const endOrSubmit = attempt.end_time || attempt.submit_time;
                  if (attempt.time_spent_seconds) {
                    timeTakenMin = Math.round(attempt.time_spent_seconds / 60);
                  } else if (attempt.start_time && endOrSubmit) {
                    const start = new Date(attempt.start_time).getTime();
                    const end = new Date(endOrSubmit).getTime();
                    if (end > start) {
                      timeTakenMin = Math.round((end - start) / 60000);
                    }
                  }

                  const studentLabel =
                    attempt.student_name ||
                    attempt.student_email ||
                    (attempt.student_id
                      ? attempt.student_id.slice(0, 8) + '...'
                      : attempt.user_id
                      ? (attempt.user_id as string).slice(0, 8) + '...'
                      : 'Unknown');

                  return (
                    <tr key={attempt.id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="py-3 px-4">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                            idx === 0
                              ? 'bg-amber-500/20 text-amber-400'
                              : idx === 1
                              ? 'bg-white/20 text-white/70'
                              : idx === 2
                              ? 'bg-orange-500/20 text-orange-400'
                              : 'bg-teal-500/10 text-teal-400'
                          }`}
                        >
                          {idx + 1}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-white" title={attempt.student_id || attempt.user_id || ''}>
                        {studentLabel}
                      </td>
                      <td className="py-3 px-4 text-sm text-white font-medium">
                        {rawScore} / {totalMarks}
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm font-medium text-white">{pct.toFixed(1)}%</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-white/50">
                        {timeTakenMin !== null ? `${timeTakenMin} min` : '--'}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                            passed ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {passed ? (
                            <>
                              <CheckCircle className="w-3 h-3" />
                              <span>Passed</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="w-3 h-3" />
                              <span>Failed</span>
                            </>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Time Series */}
      {timeSeries.length > 0 && (
        <div className="glass-card p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white">Attempt Trends</h2>
            <div className="flex items-center space-x-2">
              {[7, 14, 30, 90].map((days) => (
                <button
                  key={days}
                  onClick={() => setTimeSeriesRange({ days })}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    timeSeriesRange.days === days
                      ? 'bg-teal-500 text-white'
                      : 'bg-white/5 text-white/50 hover:text-white'
                  }`}
                >
                  {days}d
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            {timeSeries.map((point: any, idx: number) => {
              const maxAttempts = Math.max(...timeSeries.map((p: any) => p.attempts || 0), 1);
              return (
                <div key={idx} className="flex items-center space-x-3">
                  <span className="text-xs text-white/50 w-20">{point.date?.slice(5)}</span>
                  <div className="flex-1 bg-white/10 rounded-full h-2">
                    <div
                      className="bg-teal-500 h-2 rounded-full transition-all"
                      style={{ width: `${((point.attempts || 0) / maxAttempts) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs text-white/70 w-12 text-right">{point.attempts || 0}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Violations Breakdown */}
      {examViolations && (
        <div className="glass-card p-6 mb-8">
          <h2 className="text-lg font-semibold text-white mb-6">Violation Breakdown</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {(Array.isArray(examViolations) ? examViolations : []).map((v: any) => (
              <div key={v.violation_type} className="p-4 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                <div className="text-sm text-white/50 mb-1 capitalize">{(v.violation_type || '').replace(/_/g, ' ')}</div>
                <div className="text-xl font-bold text-white">{v.total_count ?? 0}</div>
                <div className="text-xs text-white/40 mt-1">{v.unique_students ?? 0} student{v.unique_students !== 1 ? 's' : ''}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Exam Details */}
      <div className="glass-card p-6">
        <h2 className="text-lg font-semibold text-white mb-6">Exam Details</h2>

        <div className="grid grid-cols-3 gap-6">
          <div>
            <div className="text-sm text-white/50 mb-1">Total Marks</div>
            <div className="text-lg font-semibold text-white">{exam.total_marks ?? exam.total_points ?? 0}</div>
          </div>
          <div>
            <div className="text-sm text-white/50 mb-1">Passing Marks</div>
            <div className="text-lg font-semibold text-white">{exam.passing_marks ?? exam.passing_score ?? 0}%</div>
          </div>
          <div>
            <div className="text-sm text-white/50 mb-1">Duration</div>
            <div className="text-lg font-semibold text-white">{exam.duration ?? exam.duration_minutes ?? 0} min</div>
          </div>
          <div>
            <div className="text-sm text-white/50 mb-1">Shuffle Questions</div>
            <div className="text-lg font-semibold text-white">
              {exam.shuffle_questions ? 'Yes' : 'No'}
            </div>
          </div>
          <div>
            <div className="text-sm text-white/50 mb-1">Shuffle Options</div>
            <div className="text-lg font-semibold text-white">
              {exam.shuffle_options ? 'Yes' : 'No'}
            </div>
          </div>
          <div>
            <div className="text-sm text-white/50 mb-1">Proctoring</div>
            <div className="text-lg font-semibold text-white">
              {(exam.is_proctored || exam.proctoring_enabled) ? 'Enabled' : 'Disabled'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
